import React, { useState, useRef, useEffect, useCallback } from 'react'
import { parsePriceInput, priceToMillions, formatModifier } from '../../utils/formatters'
import { TakePriceModifier, TAKE_PRICE_MODIFIERS } from '../../types/database'
import { useInlineEditorBehavior } from '../../hooks/useInlineEditorBehavior'

interface InlineTakePriceEditorProps {
  /** Current price value in storage format (e.g., 37000000 for $37M) */
  value: number | null | undefined
  /** Current modifier value */
  modifier: TakePriceModifier | null | undefined
  /** Field name for display/debugging purposes */
  fieldName: string
  /** Called when user saves new values */
  onSave: (newValue: number | null, newModifier: TakePriceModifier | null) => Promise<void>
  /** Called when user cancels editing */
  onCancel: () => void
  /** Optional placeholder text */
  placeholder?: string
}

/**
 * Inline editor for take price fields with modifier dropdown.
 * - Displays value in millions (e.g., "1.975" for $1.975M)
 * - Includes modifier dropdown (+, -, +/-, Motivated, Firm, Some Room)
 * - Auto-saves on blur or Enter key
 * - Cancels on Escape key
 */
export default function InlineTakePriceEditor({
  value,
  modifier,
  fieldName: _fieldName,
  onSave,
  onCancel,
  placeholder = '0.000'
}: InlineTakePriceEditorProps) {
  // Convert storage value to millions for editing
  const initialValue = priceToMillions(value)
  const [inputValue, setInputValue] = useState(
    initialValue !== null ? initialValue.toString() : ''
  )
  const [selectedModifier, setSelectedModifier] = useState<TakePriceModifier | null>(modifier ?? null)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Focus and select input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  const handleSave = useCallback(async () => {
    if (isSaving) return

    // Parse the input value (user enters millions, we store full number)
    const newValue = parsePriceInput(inputValue)

    // Check if anything actually changed
    const oldValue = value ?? null
    const oldModifier = modifier ?? null
    const valueChanged = newValue !== oldValue
    const modifierChanged = selectedModifier !== oldModifier

    if (!valueChanged && !modifierChanged) {
      onCancel()
      return
    }

    setIsSaving(true)
    try {
      await onSave(newValue, selectedModifier)
    } catch (error) {
      // Stay in edit mode on error
      setIsSaving(false)
    }
  }, [inputValue, selectedModifier, value, modifier, onSave, onCancel, isSaving])

  const { handleKeyDown, handleBlur } = useInlineEditorBehavior({
    onSave: handleSave,
    onCancel,
    containerRef,
    checkFocusWithinContainer: true,
    blurDelay: 150
  })

  return (
    <div ref={containerRef} className="absolute z-50 flex items-center gap-1 bg-white shadow-lg rounded p-1 -ml-1 -mt-1">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isSaving}
        placeholder={placeholder}
        className={`
          w-24 px-2 py-1 text-sm text-center
          border border-green-500 rounded
          focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
          ${isSaving ? 'bg-gray-100 text-gray-500' : 'bg-white'}
        `}
      />
      <select
        value={selectedModifier || ''}
        onChange={(e) => setSelectedModifier(e.target.value as TakePriceModifier || null)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isSaving}
        className={`
          w-24 px-1 py-1 text-xs
          border border-green-500 rounded
          focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
          ${isSaving ? 'bg-gray-100 text-gray-500' : 'bg-white'}
        `}
      >
        <option value="">None</option>
        {TAKE_PRICE_MODIFIERS.map((mod) => (
          <option key={mod} value={mod}>
            {formatModifier(mod) || mod}
          </option>
        ))}
      </select>
      {/* Visual indicator showing format hint */}
      <div className="absolute -bottom-4 left-1 text-[10px] text-gray-400">
        $M
      </div>
    </div>
  )
}

